export const BRAND = {
  name: 'YPFOPS 雲端工廠',
  nameEn: 'YPFOPS Cloud Factory',
  short: 'YPFOPS',
  sub: 'Cloud Factory Management',
  icon: '🏭',
}

const COLOR_DEFS = {
  light: {
    bg: '#f0f4f8',
    panel: '#ffffff',
    panelAlt: '#f8fafc',
    border: '#d1dce8',
    accent: '#1a56db',
    accentDim: '#e8f0fd',
    accentHover: '#1648c0',
    text: '#0f172a',
    muted: '#64748b',
    sub: '#334155',
    green: '#166534',
    greenBg: '#dcfce7',
    greenBorder: '#86efac',
    red: '#991b1b',
    redBg: '#fee2e2',
    redBorder: '#fca5a5',
    yellow: '#92400e',
    yellowBg: '#fef3c7',
    yellowBorder: '#fcd34d',
    sidebarBg: '#1e3a5f',
    sidebarText: '#e2eaf4',
    sidebarMuted: '#7fa3c8',
    sidebarActive: '#2563eb',
    sidebarActiveBg: '#1e40af',
  },
  dark: {
    bg: '#0f172a',
    panel: '#1e293b',
    panelAlt: '#263448',
    border: '#334155',
    accent: '#3b82f6',
    accentDim: '#1e3a5f',
    accentHover: '#2563eb',
    text: '#f1f5f9',
    muted: '#94a3b8',
    sub: '#cbd5e1',
    green: '#4ade80',
    greenBg: '#14532d',
    greenBorder: '#166534',
    red: '#f87171',
    redBg: '#450a0a',
    redBorder: '#991b1b',
    yellow: '#fbbf24',
    yellowBg: '#451a03',
    yellowBorder: '#92400e',
    sidebarBg: '#0f172a',
    sidebarText: '#e2eaf4',
    sidebarMuted: '#64748b',
    sidebarActive: '#3b82f6',
    sidebarActiveBg: '#1e3a5f',
  },
}

function toVar(key) {
  return `var(--c-${key.replace(/([A-Z])/g, m => '-' + m.toLowerCase())})`
}

export function applyTheme(isDark) {
  const colors = isDark ? COLOR_DEFS.dark : COLOR_DEFS.light
  const root = document.documentElement
  Object.entries(colors).forEach(([key, val]) => {
    root.style.setProperty(`--c-${key.replace(/([A-Z])/g, m => '-' + m.toLowerCase())}`, val)
  })
  root.setAttribute('data-theme', isDark ? 'dark' : 'light')
}

// All C values are CSS variable references — they work in inline styles and respond to applyTheme()
export const C = {
  ...Object.fromEntries(Object.keys(COLOR_DEFS.light).map(key => [key, toVar(key)])),
  sidebar: 240,
}

export const st = {
  input: {
    width: '100%',
    padding: '13px 16px',
    background: C.panel,
    border: `2px solid ${C.border}`,
    borderRadius: 10,
    color: C.text,
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  label: {
    fontSize: 13,
    color: C.sub,
    fontWeight: 600,
    letterSpacing: 0.3,
    marginBottom: 6,
    display: 'block',
  },
  btn: (v = 'primary') => ({
    padding: '14px 24px',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: 52,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 2,
    transition: 'all 0.15s',
    background:
      v === 'primary' ? C.accent
      : v === 'danger' ? C.redBg
      : v === 'warning' ? C.yellowBg
      : v === 'success' ? C.greenBg
      : '#f1f5f9',
    color:
      v === 'primary' ? '#ffffff'
      : v === 'danger' ? C.red
      : v === 'warning' ? C.yellow
      : v === 'success' ? C.green
      : C.sub,
    border:
      v === 'primary' ? `2px solid ${C.accentHover}`
      : v === 'danger' ? `2px solid ${C.redBorder}`
      : v === 'warning' ? `2px solid ${C.yellowBorder}`
      : v === 'success' ? `2px solid ${C.greenBorder}`
      : `2px solid ${C.border}`,
    boxShadow: v === 'primary' ? '0 2px 8px rgba(26,86,219,0.25)' : 'none',
  }),
  badge: t => ({
    padding: '5px 12px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 700,
    display: 'inline-block',
    background:
      t === 'in' ? C.greenBg
      : t === 'out' ? C.redBg
      : t === 'warn' ? C.redBg
      : t === 'ok' ? C.greenBg
      : t === 'pending' ? C.yellowBg
      : t === 'done' ? C.greenBg
      : '#f1f5f9',
    color:
      t === 'in' ? C.green
      : t === 'out' ? C.red
      : t === 'warn' ? C.red
      : t === 'ok' ? C.green
      : t === 'pending' ? C.yellow
      : t === 'done' ? C.green
      : C.muted,
    border: `1.5px solid ${
      t === 'in' ? C.greenBorder
      : t === 'out' ? C.redBorder
      : t === 'warn' ? C.redBorder
      : t === 'ok' ? C.greenBorder
      : t === 'pending' ? C.yellowBorder
      : t === 'done' ? C.greenBorder
      : C.border}`,
  }),
  card: {
    background: C.panel,
    border: `1.5px solid ${C.border}`,
    borderRadius: 16,
    padding: '24px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  th: {
    padding: '13px 16px',
    textAlign: 'left',
    fontSize: 12,
    color: C.muted,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    borderBottom: `2px solid ${C.border}`,
    background: C.panelAlt,
  },
  td: {
    padding: '14px 16px',
    fontSize: 15,
    borderBottom: `1px solid ${C.border}`,
    color: C.sub,
  },
  barWrap: {
    background: C.border,
    borderRadius: 6,
    height: 8,
    overflow: 'hidden',
    marginTop: 6,
  },
  toast: t => ({
    position: 'fixed',
    bottom: 80,
    right: 16,
    left: 16,
    padding: '16px 20px',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    zIndex: 9999,
    background: t === 'success' ? C.greenBg : C.redBg,
    color: t === 'success' ? C.green : C.red,
    border: `2px solid ${t === 'success' ? C.greenBorder : C.redBorder}`,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    textAlign: 'center',
  }),
}

export const MODULES = [
  { key: 'wms',         label: '倉儲管理', en: 'Warehouse',   vi: 'Kho hàng',    icon: '📦', desc: 'WMS'  },
  { key: 'production',  label: '生產管理', en: 'Production',  vi: 'Sản xuất',    icon: '🏭', desc: 'MES'  },
  { key: 'shipping',    label: '出貨管理', en: 'Shipping',    vi: 'Vận chuyển',  icon: '🚚', desc: 'SHP'  },
  { key: 'crm',         label: '客戶管理', en: 'Customers',   vi: 'Khách hàng',  icon: '👥', desc: 'CRM'  },
  { key: 'reports',     label: '報表分析', en: 'Reports',     vi: 'Báo cáo',     icon: '📊', desc: 'RPT'  },
  { key: 'maintenance', label: '機械維修', en: 'Maintenance', vi: 'Bảo trì',     icon: '🔧', desc: 'MNT'  },
  { key: 'fives',       label: '5S 管理',  en: '5S',          vi: '5S',           icon: '🏆', desc: '5S'   },
  { key: 'tasks',       label: '任務管理', en: 'Tasks',       vi: 'Nhiệm vụ',    icon: '📋', desc: 'TASK' },
  { key: 'purchasing',  label: '採購管理', en: 'Purchasing',  vi: 'Mua hàng',    icon: '🛒', desc: 'PUR'  },
  { key: 'products',    label: '商品管理', en: 'Products',    vi: 'Sản phẩm',    icon: '🗂️', desc: 'PRD'  },
  { key: 'attendance',  label: '出勤管理', en: 'Attendance',  vi: 'Chấm công',   icon: '⏰', desc: 'ATT'  },
]

export function getLabel(item, lang) {
  if (!item) return ''
  if (lang === 'en') return item.en || item.label
  if (lang === 'vi') return item.vi || item.en || item.label
  return item.label
}

export const formatDate = s => s ? new Date(s).toLocaleDateString('zh-TW') : '-'
export const formatDateTime = s => s ? new Date(s).toLocaleString('zh-TW') : '-'
